import { Auth } from 'aws-amplify';
import { Credentials } from 'aws-sdk';
import axios from 'axios';

import awsconfig from '@/aws-exports';
import serverlessExports from '@/serverless-exports';
const { Signers } = require('aws-sdk/lib/core');
const combineURLs = (baseURL: string, relativeURL: string) =>
  new URL(relativeURL, baseURL).toString();

interface SignerOptions {
  url: string;
  pathname: () => string;
  methodIndex: string;
  search: () => string;
  region: string;
  method: string;
  service: string;
  body: string;
  headers: {
    host: string;
    Authorization: string;
    'X-Amz-Date': string;
    'x-amz-security-token': string;
  };
}

type RequestWithAuthorization = Pick<
  typeof axios,
  'get' | 'post' | 'put' | 'delete'
>;

const createHeaderWithAuthorization = async (
  url: string,
  method: keyof RequestWithAuthorization,
  body?: any
) => {
  const serviceName = 'execute-api';
  const options = { url, headers: {} } as SignerOptions;

  const { host, pathname, search } = new URL(url);
  options.headers.host = host;
  options.pathname = () => pathname;
  options.methodIndex = method;
  options.search = () => (search ? search.replace(/^\?/, '') : '');
  options.region = awsconfig.aws_project_region;
  options.method = method.toUpperCase();
  options.service = serviceName;
  if (body) {
    options.body = JSON.stringify(body);
  }

  const signer = new Signers.V4(options, serviceName);
  const {
    accessKeyId,
    secretAccessKey,
    sessionToken,
  } = await Auth.currentCredentials();
  signer.addAuthorization(
    new Credentials(accessKeyId, secretAccessKey, sessionToken),
    new Date()
  );

  // avoid xht error 'Refused to set unsafe header "host"'
  const { host: _removedHost, ...headersWithoutHost } = options.headers;
  const headers: Record<string, string> = { ...headersWithoutHost };

  return headers;
};

export const requestWithAuthorization: RequestWithAuthorization = {
  async get(path, config) {
    const url = combineURLs(serverlessExports.apiGatewayCustomDomainName, path);
    const headers = await createHeaderWithAuthorization(url, 'get');
    return axios.get(url, { ...config, headers });
  },
  async post(path, data, config) {
    const url = combineURLs(serverlessExports.apiGatewayCustomDomainName, path);
    const headers = await createHeaderWithAuthorization(url, 'post', data);
    return axios.post(url, data, { ...config, headers });
  },
  async put(path, data, config) {
    const url = combineURLs(serverlessExports.apiGatewayCustomDomainName, path);
    const headers = await createHeaderWithAuthorization(url, 'put', data);
    return axios.put(url, data, { ...config, headers });
  },
  async delete(path, config) {
    const url = combineURLs(serverlessExports.apiGatewayCustomDomainName, path);
    const headers = await createHeaderWithAuthorization(url, 'delete');
    return axios.delete(url, { ...config, headers });
  },
};
